import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Palette as PaletteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Status, StatusCreateRequest, StatusUpdateRequest, StatusReorderRequest, StatusDeleteValidation } from '../../types';
import { statusApi } from '../../utils/statusApi';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface SortableStatusItemProps {
  status: Status;
  onEdit: (status: Status) => void;
  onDelete: (status: Status) => void;
  isMobile: boolean;
}

const SortableStatusItem: React.FC<SortableStatusItemProps> = ({ status, onEdit, onDelete, isMobile }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 2,
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
              },
            }}
          >
            <DragIcon />
          </Box>

          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: status.color,
              flexShrink: 0,
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {status.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={t('status.management.orderLabel', { order: status.order })}
                size="small"
                variant="outlined"
                color="primary"
              />
              {status.isDefault && (
                <Chip
                  label={t('status.management.default')}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('status.management.editTooltip')}>
              <IconButton
                size="small"
                onClick={() => onEdit(status)}
                disabled={status.isDefault}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('status.management.deleteTooltip')}>
              <IconButton
                size="small"
                onClick={() => onDelete(status)}
                disabled={status.isDefault}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const StatusManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [formData, setFormData] = useState<StatusCreateRequest>({
    name: '',
    color: '#00f5ff',
    order: 0,
  });
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Delete modal states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<Status | null>(null);
  const [deleteValidation, setDeleteValidation] = useState<StatusDeleteValidation | null>(null);
  const [selectedTransferStatusId, setSelectedTransferStatusId] = useState<string>('');
  const [deleting, setDeleting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      setError(null);
      // For now, we'll use a mock organization ID. In a real app, this would come from the user context
      const data = await statusApi.getStatuses();
      setStatuses(data.sort((a, b) => a.order - b.order));
    } catch (err: any) {
      setError(err.message || t('status.management.errors.loadError'));
      console.error('Error loading statuses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStatus = () => {
    setEditingStatus(null);
    setFormData({
      name: '',
      color: '#00f5ff',
      order: statuses.length + 1,
    });
    setDialogOpen(true);
  };

  const handleEditStatus = (status: Status) => {
    setEditingStatus(status);
    setFormData({
      name: status.name,
      color: status.color,
      order: status.order,
    });
    setDialogOpen(true);
  };

  const handleDeleteStatus = async (status: Status) => {
    if (status.isDefault) {
      toast.error(t('status.management.notifications.cannotDeleteDefault'));
      return;
    }

    try {
      // Check if status can be deleted
      const validation = await statusApi.canDeleteStatus(status.id);
      setDeleteValidation(validation);
      setStatusToDelete(status);
      setSelectedTransferStatusId('');
      setDeleteDialogOpen(true);
    } catch (err: any) {
      toast.error(err.message || t('status.management.errors.deleteValidationError'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!statusToDelete || !deleteValidation) return;

    try {
      setDeleting(true);

      if (deleteValidation.canDelete) {
        // Safe to delete directly
        await statusApi.deleteStatus(statusToDelete.id);
        toast.success(t('status.management.notifications.deleted'));
      } else {
        // Need to transfer leads first
        if (!selectedTransferStatusId) {
          toast.error(t('status.management.notifications.selectTransferStatus'));
          return;
        }

        const result = await statusApi.deleteStatusWithTransfer(statusToDelete.id, selectedTransferStatusId);
        toast.success(t('status.management.notifications.deleteWithTransfer', { count: result.transferredCount || 0 }));
      }

      setDeleteDialogOpen(false);
      await loadStatuses();
    } catch (err: any) {
      toast.error(err.message || t('status.management.errors.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!formData.name.trim()) {
      toast.error(t('status.management.notifications.nameRequired'));
      return;
    }

    try {
      setSaving(true);

      if (editingStatus) {
        await statusApi.updateStatus(editingStatus.id, formData);
        toast.success(t('status.management.notifications.updated'));
      } else {
        await statusApi.createStatus(formData);
        toast.success(t('status.management.notifications.created'));
      }

      setDialogOpen(false);
      await loadStatuses();
    } catch (err: any) {
      toast.error(err.message || t('status.management.errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = statuses.findIndex((item) => item.id === active.id);
    const newIndex = statuses.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newStatuses = arrayMove(statuses, oldIndex, newIndex);
      setStatuses(newStatuses);

      // Update order values
      const reorderRequest: StatusReorderRequest = {
        statuses: newStatuses.map((status, index) => ({
          id: status.id,
          order: index + 1,
        })),
      };

      try {
        await statusApi.reorderStatuses(reorderRequest);
        toast.success(t('status.management.notifications.reordered'));
      } catch (err: any) {
        toast.error(t('status.management.errors.reorderError'));
        // Revert on error
        await loadStatuses();
      }
    }
  };

  const handleInitializeDefaults = async () => {
    if (window.confirm(t('status.management.confirmInitialize'))) {
      try {
        setLoading(true);
        const organizationId = user?.organizationId || user?.id || 'mock-org-id';
        await statusApi.initializeDefaultStatuses(organizationId);
        toast.success(t('status.management.notifications.defaultsInitialized'));
        await loadStatuses();
      } catch (err: any) {
        toast.error(err.message || t('status.management.errors.loadError'));
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {t('status.management.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('status.management.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {statuses.length === 0 && (
            <Button
              variant="outlined"
              onClick={handleInitializeDefaults}
              startIcon={<PaletteIcon />}
            >
              {t('status.management.initializeDefault')}
            </Button>
          )}
          <Button
            variant="contained"
            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
            onClick={handleCreateStatus}
            startIcon={<AddIcon />}
          >
            {t('status.management.newStatus')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {statuses.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <PaletteIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {t('status.management.noStatusConfigured')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('status.management.noStatusDescription')}
          </Typography>
          <Button
            variant="contained"
            onClick={handleInitializeDefaults}
            startIcon={<PaletteIcon />}
          >
            {t('status.management.initializeDefaultStatus')}
          </Button>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={statuses.map(s => s.id)} strategy={verticalListSortingStrategy}>
            {statuses.map((status) => (
              <SortableStatusItem
                key={status.id}
                status={status}
                onEdit={handleEditStatus}
                onDelete={handleDeleteStatus}
                isMobile={isMobile}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <SortableStatusItem
                status={statuses.find(s => s.id === activeId)!}
                onEdit={() => { }}
                onDelete={() => { }}
                isMobile={isMobile}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Status Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingStatus ? t('status.management.editStatus') : t('status.management.newStatus')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label={t('status.management.statusName')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('status.management.statusColor')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  sx={{
                    width: 60,
                    height: 40,
                    '& input': {
                      padding: 0,
                      height: 40,
                    },
                  }}
                />
                <TextField
                  label={t('status.management.colorCode')}
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 40,
                  backgroundColor: formData.color,
                  borderRadius: 1,
                  mt: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Box>

            <TextField
              label={t('status.management.orderField')}
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              fullWidth
              helperText={t('status.management.orderHelper')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
          >
            {t('status.management.cancel')}
          </Button>
          <Button
            onClick={handleSaveStatus}
            variant="contained"
            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? t('status.management.saving') : t('status.management.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Status Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {t('status.management.deleteDialogTitle')}
        </DialogTitle>
        <DialogContent>
          {deleteValidation && statusToDelete && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={deleteValidation.canDelete ? "info" : "warning"}
                sx={{ mb: 3 }}
              >
                {deleteValidation.message}
              </Alert>

              {!deleteValidation.canDelete && deleteValidation.leadsCount > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('status.management.transferLeads', { count: deleteValidation.leadsCount })}
                  </Typography>
                  <FormControl fullWidth>
                    <RadioGroup
                      value={selectedTransferStatusId}
                      onChange={(e) => setSelectedTransferStatusId(e.target.value)}
                    >
                      {statuses
                        .filter(s => s.id !== statusToDelete.id)
                        .map((status) => (
                          <FormControlLabel
                            key={status.id}
                            value={status.id}
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: status.color,
                                  }}
                                />
                                <Typography variant="body1">{status.name}</Typography>
                              </Box>
                            }
                          />
                        ))}
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            {t('status.management.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting || (!deleteValidation?.canDelete && !selectedTransferStatusId)}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? t('status.management.deleting') : t('status.management.deleteStatus')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatusManagement;
